import type { MeetingDTO } from '../../global';
import type { Meeting } from '../../types';
import { toFileUrl } from '../../lib/fileUrl';
import { dateLocale, t } from '../../lib/i18n';

/**
 * Kablo biçimi (MeetingDTO) ile görünüm modeli (Meeting) arasındaki çeviri.
 *
 * Ayrımın sebebi: veritabanı zamanı tek bir ISO damgası olarak tutuyor,
 * arayüz ise "Bugün · 14:30 – 15:10 · 40 dk" gibi dört ayrı metin gösteriyor.
 * Bu metinleri veritabanına geri yazmak (eski mock veride olduğu gibi) tarihi
 * ayrıştırılamaz hâle getirirdi.
 */

function isSameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

function formatDateLabel(date: Date): string {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  if (isSameDay(date, now)) return t('Bugün', 'Today');
  if (isSameDay(date, yesterday)) return t('Dün', 'Yesterday');

  return date.toLocaleDateString(dateLocale(), {
    day: 'numeric',
    month: 'long',
    // Yıl yalnızca farklıysa gösterilsin.
    ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' as const } : {}),
  });
}

function formatClock(date: Date): string {
  return date.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' });
}

export function formatDuration(seconds: number): string {
  if (seconds <= 0) return t('0 dk', '0 min');
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t(`${Math.max(1, minutes)} dk`, `${Math.max(1, minutes)} min`);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? t(`${hours} sa`, `${hours} h`) : t(`${hours} sa ${rest} dk`, `${hours} h ${rest} min`);
}

/**
 * Görünen tarih, saat ve süre metinleri. Seçili dilde üretiliyor; dil
 * değişince `relocalizeMeeting` ile yeniden hesaplanıyor.
 */
export function displayFields(recordedAtISO: string, durationSeconds: number): Pick<Meeting, 'date' | 'time' | 'timeRange' | 'duration'> {
  const start = new Date(recordedAtISO);
  const end = new Date(start.getTime() + durationSeconds * 1000);
  return {
    date: formatDateLabel(start),
    time: formatClock(start),
    timeRange: durationSeconds > 0 ? `${formatClock(start)} – ${formatClock(end)}` : formatClock(start),
    duration: formatDuration(durationSeconds),
  };
}

export function relocalizeMeeting(meeting: Meeting): Meeting {
  return { ...meeting, ...displayFields(meeting.recordedAtISO, meeting.audioDurationSeconds) };
}

export function dtoToMeeting(dto: MeetingDTO): Meeting {
  return {
    id: dto.id,
    title: dto.title,
    recordedAtISO: dto.recordedAtISO,
    ...displayFields(dto.recordedAtISO, dto.durationSeconds),
    status: dto.status,
    space: dto.space,
    folderId: dto.folderId ?? null,
    participants: dto.participants,
    tags: dto.tags,
    summary: dto.summary,
    actionItems: dto.actionItems,
    craftBlocks: dto.craftBlocks,
    transcript: dto.transcript,
    audioFilePath: dto.audioFilePath,
    // S3'teki toplantıların ses adresi süreli; not açılınca ayrıca alınıyor.
    audioUrl: dto.storage === 'local' && dto.audioFilePath ? toFileUrl(dto.audioFilePath) : undefined,
    audioDurationSeconds: dto.durationSeconds,
    storage: dto.storage,
    bot: dto.bot,
    speakerMap: dto.speakerMap,
  };
}

export function meetingToDto(meeting: Meeting): MeetingDTO {
  return {
    id: meeting.id,
    title: meeting.title,
    recordedAtISO: meeting.recordedAtISO,
    durationSeconds: meeting.audioDurationSeconds,
    status: meeting.status,
    space: meeting.space,
    folderId: meeting.folderId ?? null,
    tags: meeting.tags,
    participants: meeting.participants,
    summary: meeting.summary,
    actionItems: meeting.actionItems,
    craftBlocks: meeting.craftBlocks,
    transcript: meeting.transcript,
    audioFilePath: meeting.audioFilePath,
    storage: meeting.storage,
    bot: meeting.bot,
    speakerMap: meeting.speakerMap,
  };
}
